export const TRACKED_CRYPTO = ['BTC', 'ETH', 'USDT'] as const;
export type TrackedCrypto = (typeof TRACKED_CRYPTO)[number];

export const CRYPTO_RATES_TTL_MS = 60_000;
export const CRYPTO_FORCE_FLOOR_MS = 15_000;

export const CRYPTO_BINANCE_PAGE_URL = 'https://www.binance.com/en/markets';
export const CRYPTO_COINGECKO_PAGE_URL = 'https://www.coingecko.com';

export type CryptoSource = 'binance' | 'coingecko' | 'manual';

export interface CryptoQuote {
  code: string;
  usd: number;
  changePct?: number | null;
}

export interface CryptoRatesSnapshot {
  id: string;
  source: CryptoSource;
  sourceUrl: string;
  fetchedAt: string;
  quotes: CryptoQuote[];
}

function normalizeCode(code: string): string {
  return String(code ?? '').trim().toUpperCase();
}

export function isCryptoSnapshotFresh(
  existing: Pick<CryptoRatesSnapshot, 'fetchedAt'> | null | undefined,
  nowMs = Date.now(),
  ttlMs = CRYPTO_RATES_TTL_MS,
): boolean {
  if (!existing?.fetchedAt) return false;
  const fetchedMs = Date.parse(existing.fetchedAt);
  if (!Number.isFinite(fetchedMs)) return false;
  return nowMs - fetchedMs < ttlMs;
}

export function pickTrackedCrypto(quotes: CryptoQuote[]): CryptoQuote[] {
  const byCode = new Map(
    quotes.map((quote) => [normalizeCode(quote.code), quote] as const),
  );
  return TRACKED_CRYPTO.flatMap((code) => {
    const quote = byCode.get(code);
    return quote ? [quote] : [];
  });
}

export function formatUsdAmount(value: number): string {
  const abs = Math.abs(value);
  const maximumFractionDigits = abs >= 1000 ? 2 : abs >= 1 ? 4 : 6;
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  });
}

/** Derive CUP equivalent from the informal USD rate (informational only). */
export function cryptoCupValue(
  usd: number,
  usdCupRate: number | null | undefined,
): number | null {
  if (!Number.isFinite(usd) || usd <= 0) return null;
  if (usdCupRate == null || !Number.isFinite(usdCupRate) || usdCupRate <= 0) return null;
  return usd * usdCupRate;
}

export function cryptoSourceLabel(source: CryptoSource | string | null | undefined): string {
  if (source === 'binance') return 'Binance';
  if (source === 'coingecko') return 'CoinGecko';
  return 'manual';
}

export function cryptoAgeSeconds(
  fetchedAt: string | null | undefined,
  nowMs = Date.now(),
): number | null {
  if (!fetchedAt) return null;
  const fetchedMs = Date.parse(fetchedAt);
  if (!Number.isFinite(fetchedMs)) return null;
  return Math.max(0, Math.floor((nowMs - fetchedMs) / 1000));
}
